// Pure helpers for the rdrive_propose_task_set_profile_deployment frontend
// tool: resolve which profile revision a deployment should target and validate
// the proposed parent tasks before anything is shown to the user. Error
// strings are written for the agent — they say what is wrong and how to fix
// it, since the agent's only recovery path is re-proposing with better args.

/**
 * Extract the useful part of an API (axios) error: HTTP status + response
 * body. `err.message` alone is "Request failed with status code 422" — the
 * server's actual reason lives in err.response.data, and both the agent and
 * the user need it to react correctly.
 */
export function describeApiError(err) {
  const status = err?.response?.status;
  let detail = err?.response?.data;
  if (detail != null && typeof detail === 'object') {
    // problem+json: detail/title carry the actual validation lines (one per
    // task on a 422) — far more actionable than the stringified envelope.
    if (detail.detail || detail.title) {
      detail = detail.detail || detail.title;
    } else {
      try {
        detail = JSON.stringify(detail);
      } catch {
        detail = String(detail);
      }
    }
  }
  const parts = [];
  if (status) parts.push(`HTTP ${status}`);
  if (detail) parts.push(String(detail).slice(0, 2000));
  if (parts.length === 0) parts.push(err?.message || String(err));
  return parts.join(' — ');
}

/**
 * Full pre-flight for a deployment proposal: validate args, resolve the
 * published revision, gather existing deployments, fetch the target schedule's
 * tree (unless the caller already holds it), and validate the targets.
 *
 * Returns { error } with an agent-actionable message, or
 * { published, family, targets } ready for the confirmation dialog.
 *
 * @param {Object} args - The frontend tool args
 * @param {Object} ctx
 * @param {Object} ctx.apiClient - ApiClient instance
 * @param {string} ctx.projectCode
 * @param {string|null} [ctx.loadedScheduleId] - Schedule whose tree the caller holds
 * @param {Array|null} [ctx.loadedTasks] - That schedule's nested task tree
 */
export async function prepareDeploymentProposal(
  args,
  { apiClient, projectCode, loadedScheduleId = null, loadedTasks = null }
) {
  const argErrors = [];
  if (!args?.scheduleId) argErrors.push('scheduleId is required');
  if (!Array.isArray(args?.parentTaskIds) || args.parentTaskIds.length === 0) {
    argErrors.push('parentTaskIds must be a non-empty array of task ids');
  }
  if (!args?.taskSetProfileId && !args?.taskSetProfileCode) {
    argErrors.push('Provide taskSetProfileId or taskSetProfileCode to identify the profile');
  }
  if (argErrors.length > 0) {
    return { error: `Invalid deployment proposal: ${argErrors.join('; ')}` };
  }

  const profiles = await apiClient.get(`/api/${projectCode}/task-set-profiles`);
  const resolved = resolvePublishedRevision(profiles || [], args);
  if (resolved.error) return resolved;
  const { published, family } = resolved;

  // Existing deployments across every published revision of the family — a
  // task deployed from an older revision must not receive the profile again.
  const publishedIds = family.filter((p) => p.isPublished === true).map((p) => p.id);
  const deploymentLists = await Promise.all(
    publishedIds.map((id) =>
      apiClient.get(`/api/${projectCode}/task-set-profiles/${id}/deployments`).catch(() => [])
    )
  );
  const deployedParentTaskIds = deploymentLists
    .flat()
    .map((d) => d?.parentTaskId)
    .filter(Boolean);

  const nestedTasks =
    loadedTasks && loadedScheduleId === args.scheduleId
      ? loadedTasks
      : await apiClient.get(`/api/${projectCode}/tasks?scheduleIds=${args.scheduleId}&nested=true`);

  const { targets, errors } = validateDeploymentTargets({
    parentTaskIds: args.parentTaskIds,
    nestedTasks: Array.isArray(nestedTasks) ? nestedTasks : [],
    profile: published,
    family,
    deployedParentTaskIds
  });
  if (errors.length > 0) {
    return {
      error:
        'Deployment proposal rejected — nothing was shown to the user. ' +
        'Fix these and re-propose:\n- ' + errors.join('\n- ')
    };
  }

  return { published, family, targets };
}

/**
 * Resolve the published revision a deployment must target.
 *
 * Deployments may only reference published revisions (drafts are rejected by
 * the API and hidden by the RDrive UI), so given any revision id or a profile
 * code, find the profile family and pick its published revision — preferring
 * the `current` one, falling back to the last published revision in list
 * order.
 *
 * @param {Array} profiles - Raw list from GET /task-set-profiles (all revisions)
 * @param {Object} ref - { taskSetProfileId, taskSetProfileCode } (one required)
 * @returns {{ published: Object, family: Array } | { error: string }}
 */
export function resolvePublishedRevision(profiles, { taskSetProfileId, taskSetProfileCode } = {}) {
  const list = profiles || [];

  let code = taskSetProfileCode || null;
  if (taskSetProfileId) {
    const byId = list.find((p) => p?.id === taskSetProfileId);
    if (!byId) {
      return { error: `No task set profile found with id ${taskSetProfileId}` };
    }
    code = byId.code;
  }
  if (!code) {
    return { error: 'Provide taskSetProfileId or taskSetProfileCode to identify the profile' };
  }

  const family = list.filter((p) => p?.code === code);
  if (family.length === 0) {
    return { error: `No task set profile found with code "${code}"` };
  }

  const publishedRevisions = family.filter((p) => p.isPublished === true);
  if (publishedRevisions.length === 0) {
    return {
      error:
        `Task set profile "${code}" exists but has no published revision — it is a draft and drafts ` +
        'cannot be deployed. Publish it first (propose the profile commit with publish: true on the ' +
        'Task Set Profiles tab), then re-propose the deployment.'
    };
  }

  const published =
    publishedRevisions.find((p) => p.current === true) ||
    publishedRevisions[publishedRevisions.length - 1];
  return { published, family };
}

/**
 * Validate proposed deployment targets against the schedule's task tree.
 *
 * Mirrors the RDrive SPA's target eligibility rules: leaf tasks only (no
 * children), no subtasks, structure-editable, and not already carrying a
 * deployment of this profile.
 *
 * @param {Object} params
 * @param {Array<string>} params.parentTaskIds - Proposed target task ids
 * @param {Array} params.nestedTasks - The schedule's nested task tree
 * @param {Object} params.profile - The published revision being deployed
 * @param {Array} params.family - All revisions of the profile (for deployed-marker checks)
 * @param {Array<string>} params.deployedParentTaskIds - Parent ids from existing deployments
 * @returns {{ targets: Array, errors: Array<string> }}
 */
export function validateDeploymentTargets({
  parentTaskIds,
  nestedTasks,
  profile,
  family = [],
  deployedParentTaskIds = []
}) {
  const byId = new Map();
  const collect = (tasks) => {
    for (const task of tasks || []) {
      if (task?.id != null) byId.set(task.id, task);
      collect(task?.children);
    }
  };
  collect(nestedTasks);

  const familyIds = new Set([profile?.id, ...family.map((p) => p?.id)].filter(Boolean));
  const deployedIds = new Set(deployedParentTaskIds);

  const targets = [];
  const errors = [];
  const seen = new Set();

  for (const id of parentTaskIds || []) {
    if (seen.has(id)) {
      errors.push(`Task ${id} appears more than once in parentTaskIds`);
      continue;
    }
    seen.add(id);

    const task = byId.get(id);
    if (!task) {
      errors.push(`Task ${id} is not in the schedule`);
      continue;
    }

    const label = `Task ${task.code || id} "${task.title || ''}"`;
    if (task.children?.length > 0) {
      errors.push(`${label} is not a leaf task — deploy targets must be leaves with no children`);
      continue;
    }
    if (task.subtasks?.length > 0) {
      errors.push(`${label} already has subtasks — deploy targets must have none`);
      continue;
    }
    if (task.lock && task.lock.structureEditable === false) {
      errors.push(`${label} is structure-locked${task.lock.key ? ` (key "${task.lock.key}")` : ''}`);
      continue;
    }
    const ownDeployedId =
      task.meta?.selectedTaskSetProfileId || task.meta?.taskSetProfileMeta?.taskSetProfileId;
    if (deployedIds.has(id) || (ownDeployedId && familyIds.has(ownDeployedId))) {
      errors.push(`${label} already has profile "${profile?.code}" deployed`);
      continue;
    }

    targets.push(task);
  }

  return { targets, errors };
}
